import { useLanguage } from '../../i18n';
import { PrimaryButton } from './PrimaryButton';
import { ScreenDescription } from './ScreenDescription';
import { ScreenTitle } from './ScreenTitle';
import { SecondaryButton } from './SecondaryButton';
import { VerticalStack } from './VerticalStack';

interface PipelineRecoveryNoticeProps {
  retrying: boolean;
  onRetry: () => void;
  /**
   * The selfie passed liveness but its age could not be estimated (a
   * technical failure, not an age decision). The customer can take another
   * selfie, when Portal still allows a retry, or continue with an ID check
   * that reuses the selfie already taken.
   */
  ageEstimateUnavailable?: {
    retryAvailable: boolean;
    onUseId: () => void;
  };
}

/** Calm, non-terminal recovery state for the required L2 stage handoff. */
export function PipelineRecoveryNotice({
  retrying,
  onRetry,
  ageEstimateUnavailable,
}: PipelineRecoveryNoticeProps) {
  const { t } = useLanguage();

  if (ageEstimateUnavailable) {
    const { retryAvailable, onUseId } = ageEstimateUnavailable;
    return (
      <section
        aria-labelledby="pipeline-recovery-title"
        data-testid="age-estimate-unavailable-notice"
      >
        <VerticalStack spacing="lg" align="center">
          <div id="pipeline-recovery-title">
            <ScreenTitle size="lg">{t('ageEstimateUnavailable.title')}</ScreenTitle>
          </div>
          <ScreenDescription color="primary">
            {t(
              retryAvailable
                ? 'ageEstimateUnavailable.description'
                : 'ageEstimateUnavailable.descriptionIdOnly'
            )}
          </ScreenDescription>
          {retryAvailable && (
            <PrimaryButton onClick={onRetry} disabled={retrying}>
              {retrying
                ? t('pipelineRecovery.retryingAction')
                : t('common.tryAgain')}
            </PrimaryButton>
          )}
          {retryAvailable ? (
            <SecondaryButton onClick={onUseId} disabled={retrying}>
              {t('ageEstimateUnavailable.useId')}
            </SecondaryButton>
          ) : (
            <PrimaryButton onClick={onUseId} disabled={retrying}>
              {t('ageEstimateUnavailable.useId')}
            </PrimaryButton>
          )}
        </VerticalStack>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="pipeline-recovery-title"
      data-testid="pipeline-recovery-notice"
    >
      <VerticalStack spacing="lg" align="center">
        <div id="pipeline-recovery-title">
          <ScreenTitle size="lg">{t('pipelineRecovery.title')}</ScreenTitle>
        </div>
        <ScreenDescription color="primary">
          {t('pipelineRecovery.description')}
        </ScreenDescription>
        <ScreenDescription>
          {t(
            retrying
              ? 'pipelineRecovery.retrying'
              : 'pipelineRecovery.readyToRetry'
          )}
        </ScreenDescription>
        <PrimaryButton onClick={onRetry} disabled={retrying}>
          {retrying
            ? t('pipelineRecovery.retryingAction')
            : t('common.tryAgain')}
        </PrimaryButton>
      </VerticalStack>
    </section>
  );
}
